import { logger } from '../logger';

/**
 * Debug level configuration for API logging / APIログのデバッグレベル設定
 *
 * @description
 * Supports both build-time (import.meta.env) and runtime (process.env) configuration.
 *
 * ビルド時(import.meta.env)と実行時(process.env)の両方の設定をサポートします。
 *
 * Debug levels / デバッグレベル:
 * - 0 or falsy: No debug logging / デバッグログなし
 * - 1: Show headers only / ヘッダーのみ表示
 * - 2: Show headers + request body + response body / ヘッダー + リクエストボディ + レスポンスボディを表示
 *
 * @private
 */
const getDebugLevel = (value: unknown): number => {
  if (!value) return 0;
  const str = String(value).toLowerCase().trim();

  // Parse as number
  const num = parseInt(str, 10);
  if (!isNaN(num)) return num;

  // Parse as boolean (backward compatibility)
  if (str === 'true' || str === 'yes') return 1;
  return 0;
};

/**
 * Current debug level determined from environment variables / 環境変数から決定された現在のデバッグレベル
 * @private
 */
const DEBUG_LEVEL = (() => {
  // Prefer server-side process.env if available
  if (typeof process !== 'undefined' && process.env?.DEBUG) {
    return getDebugLevel(process.env.DEBUG);
  }
  // Vite build-time environment variables (VITE_ prefix)
  if (import.meta.env.VITE_DEBUG) {
    return getDebugLevel(import.meta.env.VITE_DEBUG);
  }
  // Custom configuration fallback
  if (import.meta.env.DEBUG) {
    return getDebugLevel(import.meta.env.DEBUG);
  }
  return 0;
})();

/**
 * Log API request details based on debug level / デバッグレベルに基づいてAPIリクエストの詳細をログ出力
 *
 * @description
 * Logs API request and response information with configurable verbosity.
 * The amount of information logged depends on the DEBUG_LEVEL setting.
 *
 * 設定可能な詳細度でAPIリクエストとレスポンスの情報をログに記録します。
 * ログに記録される情報量はDEBUG_LEVEL設定に依存します。
 *
 * @param data - Request/response data to log / ログ出力するリクエスト/レスポンスデータ
 * @param data.url - The request URL / リクエストURL
 * @param data.method - HTTP method (defaults to 'GET') / HTTPメソッド（デフォルトは'GET'）
 * @param data.headers - Request headers (logged at level 1+) / リクエストヘッダー（レベル1以上でログ出力）
 * @param data.body - Request body (logged at level 2+) / リクエストボディ（レベル2以上でログ出力）
 * @param data.status - HTTP response status code / HTTPレスポンスステータスコード
 * @param data.duration - Request duration in milliseconds / リクエスト所要時間（ミリ秒）
 * @param data.error - Error information if request failed / リクエストが失敗した場合のエラー情報
 * @param data.responseHeaders - Response headers (logged at level 1+) / レスポンスヘッダー（レベル1以上でログ出力）
 * @param data.responseBody - Response body (logged at level 2+) / レスポンスボディ（レベル2以上でログ出力）
 *
 * @example
 * ```typescript
 * logApiRequest({
 *   url: 'https://api.example.com/users',
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: { name: 'John' },
 *   status: 200,
 *   duration: 150.5,
 *   responseBody: { id: 123, name: 'John' }
 * });
 * ```
 */
export const logApiRequest = (data: {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  status?: number;
  duration?: number;
  error?: unknown;
  responseHeaders?: Record<string, string>;
  responseBody?: unknown;
}) => {
  // Skip logging in browser environment
  if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
    return;
  }

  const { url, method = 'GET', headers, body, status, duration, error, responseHeaders, responseBody } = data;

  // Build log data based on DEBUG_LEVEL
  const buildLogData = (baseData: any) => {
    const logData = { ...baseData };

    // Level 1+: Include headers
    if (DEBUG_LEVEL >= 1 && headers) {
      logData.headers = headers;
    }
    if (DEBUG_LEVEL >= 1 && responseHeaders) {
      logData.responseHeaders = responseHeaders;
    }

    // Level 2+: Include request and response bodies
    if (DEBUG_LEVEL >= 2) {
      if (body !== undefined) {
        logData.requestBody = body;
      }
      if (responseBody !== undefined) {
        logData.responseBody = responseBody;
      }
    }

    return logData;
  };

  if (error) {
    const errorData = buildLogData({
      type: 'api_request_error',
      url,
      method,
      status,
      error: error instanceof Error ? error.message : String(error),
      duration: duration ? `${duration.toFixed(2)}ms` : undefined,
    });
    logger.error(errorData, `API Error: ${method} ${url}`);
  } else {
    const successData = buildLogData({
      type: 'api_request_success',
      url,
      method,
      status,
      duration: duration ? `${duration.toFixed(2)}ms` : undefined,
    });
    logger.info(successData, `API Success: ${method} ${url}`);
  }
};

/**
 * Current debug level (0, 1, or 2) / 現在のデバッグレベル（0、1、2）
 */
export { DEBUG_LEVEL };
